// The Trades-page KPI strip (WickScope "Refined" header band).
//
// Five tiles sourced from the day-sessions endpoint `totals` (computed over the
// full filtered set, server-side — never derived from the visible page): Net
// P&L, Win rate, Trades (W/L), Best trade, and Worst trade. Purely
// presentational; the page owns the data fetch.

import type { ReactNode } from "react";

import type { components } from "../../api/schema";
import { formatSignedMoney } from "../../lib/money";

type Totals = components["schemas"]["DaySessionTotalsResponse"];

type Tone = "pos" | "neg" | "neutral";

function toneClass(tone: Tone): string {
  if (tone === "pos") return "text-success";
  if (tone === "neg") return "text-danger";
  return "text-on-surface";
}

function signedTone(amount: string | null | undefined): Tone {
  if (amount === null || amount === undefined) return "neutral";
  const n = Number(amount);
  if (!Number.isFinite(n) || n === 0) return "neutral";
  return n > 0 ? "pos" : "neg";
}

function KpiTile({
  label,
  value,
  sub,
  tone = "neutral",
  bar,
}: {
  label: string;
  value: string;
  sub?: ReactNode;
  tone?: Tone;
  // Optional 0..1 progress bar rendered under the value (win-rate tile).
  bar?: number;
}): ReactNode {
  return (
    <div className="flex flex-col gap-1 rounded-card bg-surface-container p-4">
      <div className="text-xs font-medium uppercase tracking-wide text-on-surface-variant">
        {label}
      </div>
      <div className={`font-data text-2xl font-semibold tabular-nums ${toneClass(tone)}`}>
        {value}
      </div>
      {bar !== undefined ? (
        <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-surface-container-low">
          <div
            className="h-full rounded-full bg-success"
            style={{ width: `${Math.round(Math.min(1, Math.max(0, bar)) * 100)}%` }}
          />
        </div>
      ) : null}
      {sub !== undefined ? (
        <div className="text-[11px] text-on-surface-muted">{sub}</div>
      ) : null}
    </div>
  );
}

function winRatePct(winRate: string): string {
  const n = Number(winRate);
  if (!Number.isFinite(n)) return "—";
  return `${Math.round(n * 100)}%`;
}

function extremum(
  trade: Totals["best_trade"],
  currency: string,
): { value: string; sub: string } {
  if (trade === null || trade === undefined) {
    return { value: "—", sub: "" };
  }
  return {
    value: formatSignedMoney(trade.pnl_amount, { currency }),
    sub: trade.symbol ?? "—",
  };
}

export function TradesKpiStrip({ totals }: { totals: Totals }): ReactNode {
  const currency = totals.currency;
  const record = `${totals.win_count}W · ${totals.loss_count}L`;
  const winRate = Number(totals.win_rate);
  const best = extremum(totals.best_trade, currency);
  const worst = extremum(totals.worst_trade, currency);

  return (
    <section
      aria-label="Trades summary"
      className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5"
    >
      <KpiTile
        label="Net P&L"
        value={formatSignedMoney(totals.net_pnl_amount, { currency })}
        sub="session total"
        tone={signedTone(totals.net_pnl_amount)}
      />
      <KpiTile
        label="Win rate"
        value={winRatePct(totals.win_rate)}
        sub={record}
        bar={Number.isFinite(winRate) ? winRate : 0}
      />
      <KpiTile
        label="Trades"
        value={String(totals.trade_count)}
        sub={record}
      />
      <KpiTile label="Best trade" value={best.value} sub={best.sub} tone="pos" />
      <KpiTile label="Worst trade" value={worst.value} sub={worst.sub} tone="neg" />
    </section>
  );
}

// Exposed so the page can render the same shell while the strip data loads.
export function TradesKpiStripSkeleton(): ReactNode {
  return (
    <div
      aria-hidden="true"
      className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5"
    >
      {Array.from({ length: 5 }).map((_, index) => (
        <div key={index} className="h-24 animate-pulse rounded-card bg-surface-container" />
      ))}
    </div>
  );
}
